import React, { useEffect, useState } from "react";
import { Wallet, Plus, Receipt, PiggyBank, ClipboardCheck, HandCoins, ScrollText, Users, DollarSign } from "lucide-react";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHero, Section, StatCard, Badge, Button, Input, Select, Field, DataTable, EmptyState } from "@/components/premium/UI";
import { Pattern } from "@/components/premium/Patterns";
import { toast } from "sonner";
import MultiCurrencySale from "@/pages/MultiCurrencySale";
import OutstandingMc from "@/pages/OutstandingMc";

const TABS = [
  { key: "new_mc",         label: "New sale (multi-currency)", icon: DollarSign },
  { key: "register",       label: "Sales register",  icon: Receipt },
  { key: "receipts",       label: "Receipts",         icon: ScrollText },
  { key: "outstanding",    label: "Outstanding",      icon: HandCoins },
  { key: "deposits",       label: "Safe deposits",    icon: PiggyBank },
  { key: "reconciliation", label: "Reconciliation",   icon: ClipboardCheck },
  { key: "customers",      label: "Customers",        icon: Users },
];

const money = (n) => n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CaregiverSales() {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_sales:write");
  const [tab, setTab] = useState("register");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [reconciliation, setReconciliation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [showForm, setShowForm] = useState(false);

  useEffect(() => { api.get("/sales/dashboard").then(r => setStats(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    if (tab === "new_mc") {
      setLoading(false);
      setShowForm(false); setForm({});
      return;
    }
    if (tab === "outstanding") {
      // Outstanding tab renders its own multi-currency-aware component (see below).
      setLoading(false);
      setShowForm(false); setForm({});
      return;
    }
    if (tab === "reconciliation") {
      api.get("/sales/reconciliation").then(r => setReconciliation(r.data)).catch(() => setReconciliation(null)).finally(() => setLoading(false));
    } else {
      const endpoint = { register: "register", receipts: "receipts", deposits: "deposits", customers: "customers" }[tab] || tab;
      api.get(`/sales/${endpoint}`).then(r => setRows(r.data || [])).catch(() => setRows([])).finally(() => setLoading(false));
    }
    setShowForm(false); setForm({});
  }, [tab]);

  async function submit(e) {
    e.preventDefault();
    try {
      const endpoint = tab === "customers" ? "customers" : tab === "deposits" ? "deposits" : "register";
      await api.post(`/sales/${endpoint}`, form);
      toast.success("Saved");
      const { data } = await api.get(`/sales/${endpoint}`);
      setRows(data || []);
      const s = await api.get("/sales/dashboard"); setStats(s.data);
      setShowForm(false); setForm({});
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  }

  const canAdd = canWrite && ["register", "deposits", "customers"].includes(tab);

  return (
    <div className="space-y-6" data-testid="sales-page">
      <PageHero
        eyebrow="Sales & Cash Reconciliation"
        title="Poultry sales & cash"
        subtitle="Daily sales register, customer receipts, safe deposits, outstanding balances and full cash reconciliation."
        moduleKind="finance"
        actions={canAdd && <Button variant="primary" onClick={() => setShowForm(v => !v)} data-testid="sales-add-btn"><Plus className="w-4 h-4" /> Add</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <StatCard label="Sales today" value={money(stats?.sales_today)} icon={Wallet} tone="accent" testid="sales-stat-today" />
        <StatCard label="This week" value={money(stats?.sales_this_week)} icon={Wallet} tone="info" testid="sales-stat-week" />
        <StatCard label="This month" value={money(stats?.sales_this_month)} icon={Wallet} tone="primary" testid="sales-stat-month" />
        <StatCard label="Outstanding" value={money(stats?.outstanding_balances)} icon={HandCoins} tone={stats?.outstanding_balances > 0 ? "danger" : "success"} testid="sales-stat-outstanding" />
      </div>

      <Section className="relative overflow-hidden">
        <Pattern kind="finance" />
        <div className="relative">
          <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-full border border-border bg-muted/40 w-fit">
            {TABS.filter(t => t.key !== "new_mc" || canWrite).map(t => (
              <button
                key={t.key}
                data-testid={`sales-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${tab === t.key ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}
              >
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {showForm && <SForm tab={tab} form={form} setForm={setForm} submit={submit} onCancel={() => setShowForm(false)} />}

          {tab === "new_mc" ? (
            <MultiCurrencySale onCompleted={async () => {
              try { const s = await api.get("/sales/dashboard"); setStats(s.data); } catch { /* silent */ }
            }} />
          ) : tab === "outstanding" ? (
            <OutstandingMc />
          ) : loading ? (
            <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-10 bg-muted/40 rounded-lg" />)}</div>
          ) : tab === "reconciliation" ? (
            <ReconciliationView r={reconciliation} />
          ) : rows.length === 0 ? (
            <EmptyState icon={Wallet} title={`No ${tab} yet`} description={canWrite ? "Add the first record." : "Records will appear here once created."} />
          ) : (
            <DataTable testid={`sales-table-${tab}`} columns={cols(tab)} rows={rows} />
          )}
        </div>
      </Section>
    </div>
  );
}

function cols(tab) {
  switch (tab) {
    case "register":
    case "receipts": return [
      { header: "Date", key: "sale_date" },
      { header: "Customer", cell: r => <span className="font-medium">{r.customer}</span> },
      { header: "Invoice", cell: r => <Badge variant="primary">{r.invoice_number}</Badge> },
      { header: "Birds", key: "birds_sold" },
      { header: "Total", cell: r => money(r.total) },
      { header: "Paid", cell: r => money(r.amount_paid) },
      { header: "Balance", cell: r => <span className={Number(r.balance) > 0 ? "text-destructive font-medium" : ""}>{money(r.balance)}</span> },
      { header: "Status", cell: r => <Badge variant={r.payment_status === "paid" ? "healthy" : r.payment_status === "partial" ? "pending" : "critical"}>{r.payment_status}</Badge> },
    ];
    case "outstanding": return [
      { header: "Date", key: "sale_date" },
      { header: "Customer", cell: r => <span className="font-medium">{r.customer}</span> },
      { header: "Invoice", key: "invoice_number" },
      { header: "Total", cell: r => money(r.total) },
      { header: "Paid", cell: r => money(r.amount_paid) },
      { header: "Balance", cell: r => <span className="text-destructive font-semibold">{money(r.balance)}</span> },
    ];
    case "deposits": return [
      { header: "Date", key: "deposit_date" },
      { header: "Amount", cell: r => <span className="font-semibold">{money(r.amount)}</span> },
      { header: "Method", cell: r => <Badge variant="primary">{r.method}</Badge> },
      { header: "Reference", key: "reference" },
      { header: "Captured by", key: "captured_by" },
      { header: "Notes", cell: r => <span className="line-clamp-1">{r.notes}</span> },
    ];
    case "customers": return [
      { header: "Name", cell: r => <span className="font-medium">{r.name}</span> },
      { header: "Phone", key: "phone" },
      { header: "Email", key: "email" },
      { header: "Address", key: "address" },
    ];
    default: return [];
  }
}

function ReconciliationView({ r }) {
  if (!r) return <EmptyState icon={ClipboardCheck} title="No reconciliation data" description="Record sales and deposits to see today's reconciliation." />;
  const balanced = r.status === "balanced";
  return (
    <div className="space-y-5" data-testid="sales-reconciliation-view">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-border p-4">
          <div className="eyebrow">Sales count</div>
          <div className="font-heading text-2xl font-bold mt-1">{r.sales_count}</div>
        </div>
        <div className="rounded-2xl border border-border p-4">
          <div className="eyebrow">Cash received</div>
          <div className="font-heading text-2xl font-bold mt-1 text-success">{money(r.cash_received)}</div>
        </div>
        <div className="rounded-2xl border border-border p-4">
          <div className="eyebrow">Deposited</div>
          <div className="font-heading text-2xl font-bold mt-1 text-primary">{money(r.deposited)}</div>
        </div>
        <div className={`rounded-2xl border p-4 ${balanced ? "border-success/40 bg-success/5" : "border-accent/40 bg-accent/5"}`}>
          <div className="eyebrow">On hand</div>
          <div className={`font-heading text-2xl font-bold mt-1 ${balanced ? "text-success" : "text-accent"}`}>{money(r.on_hand)}</div>
          <Badge variant={balanced ? "healthy" : "pending"} className="mt-2">{balanced ? "Balanced" : "Reconciliation pending"}</Badge>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section title={`Today's sales (${r.sales?.length || 0})`}>
          <DataTable columns={cols("register")} rows={r.sales || []} empty="No sales today." />
        </Section>
        <Section title={`Today's deposits (${r.deposits?.length || 0})`}>
          <DataTable columns={cols("deposits")} rows={r.deposits || []} empty="No deposits today." />
        </Section>
      </div>
    </div>
  );
}

function SForm({ tab, form, setForm, submit, onCancel }) {
  const upd = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={submit} className="rounded-2xl border border-border bg-muted/30 p-5 mb-5 grid grid-cols-1 md:grid-cols-3 gap-3">
      {tab === "register" && (<>
        <Field label="Sale date"><Input type="date" value={form.sale_date || new Date().toISOString().slice(0, 10)} onChange={e => upd("sale_date", e.target.value)} required data-testid="sales-form-date" /></Field>
        <Field label="Customer"><Input value={form.customer || ""} onChange={e => upd("customer", e.target.value)} required data-testid="sales-form-customer" /></Field>
        <Field label="Birds sold"><Input type="number" value={form.birds_sold || ""} onChange={e => upd("birds_sold", parseInt(e.target.value))} required /></Field>
        <Field label="Avg weight (kg)"><Input type="number" step="0.01" value={form.avg_weight_kg || ""} onChange={e => upd("avg_weight_kg", parseFloat(e.target.value))} required /></Field>
        <Field label="Price / kg"><Input type="number" step="0.01" value={form.price_per_kg || ""} onChange={e => upd("price_per_kg", parseFloat(e.target.value))} required /></Field>
        <Field label="Payment">
          <Select value={form.payment_status || "paid"} onChange={e => upd("payment_status", e.target.value)}>
            {["paid", "partial", "credit"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Amount paid"><Input type="number" step="0.01" value={form.amount_paid ?? ""} onChange={e => upd("amount_paid", parseFloat(e.target.value))} /></Field>
        <Field label="Receipt ref"><Input value={form.receipt_ref || ""} onChange={e => upd("receipt_ref", e.target.value)} /></Field>
        <Field label="Notes" className="md:col-span-3"><Input value={form.notes || ""} onChange={e => upd("notes", e.target.value)} /></Field>
      </>)}
      {tab === "deposits" && (<>
        <Field label="Deposit date"><Input type="date" value={form.deposit_date || new Date().toISOString().slice(0, 10)} onChange={e => upd("deposit_date", e.target.value)} required /></Field>
        <Field label="Amount"><Input type="number" step="0.01" value={form.amount || ""} onChange={e => upd("amount", parseFloat(e.target.value))} required data-testid="deposit-form-amount" /></Field>
        <Field label="Method">
          <Select value={form.method || "safe"} onChange={e => upd("method", e.target.value)}>
            {["safe", "bank", "mobile_money"].map(v => <option key={v}>{v}</option>)}
          </Select>
        </Field>
        <Field label="Reference"><Input value={form.reference || ""} onChange={e => upd("reference", e.target.value)} /></Field>
        <Field label="Notes" className="md:col-span-2"><Input value={form.notes || ""} onChange={e => upd("notes", e.target.value)} /></Field>
      </>)}
      {tab === "customers" && (<>
        <Field label="Name"><Input value={form.name || ""} onChange={e => upd("name", e.target.value)} required /></Field>
        <Field label="Phone"><Input value={form.phone || ""} onChange={e => upd("phone", e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email || ""} onChange={e => upd("email", e.target.value)} /></Field>
        <Field label="Address" className="md:col-span-3"><Input value={form.address || ""} onChange={e => upd("address", e.target.value)} /></Field>
      </>)}
      <div className="md:col-span-3 flex justify-end gap-2 mt-2">
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
        <Button type="submit" variant="primary" data-testid="sales-form-submit">Save</Button>
      </div>
    </form>
  );
}
